<template>
  <!-- render modal at top level to modal-destination -->
  <portal to="modal-destination" :order="order">
    <transition name="modal-slide" appear>
      <div
        class="aha-modal"
        :class="[{'aha-modal__backdrop-blur': blurBackdrop}, classesModal]"
      >
        <div
          class="aha-modal__backdrop"
          @click="onClickBackdrop()"
        />

        <div
          class="aha-modal__content"
          :class="[
            `aha-modal__content--${size}`,
            {
              'aha-modal__no-scroll': noScroll,
              'aha-modal-mobile-max-height': mobileMaxHeight,
              'visible-overflow': visibleOverflow
            }
          ]"
          :style="{ width, 'box-shadow': boxShadow }"
        >
          <div class="aha-modal__header" v-if="!hideHeader && !showCustomHeader">
            <slot name="header">
              <h4 :class="titlePosition" class="aha-modal__header--text">
                <aha-svg
                  size="lg"
                  v-if="backable"
                  name="arrow-tail"
                  view-box="0 0 18 19"
                  @click="handleBackButtonClick()"
                  class="flex-shrink-0 mr-3 align-middle cursor-pointer back-button"
                />
                <aha-icon
                  v-if="iconBeforeModalTitle"
                  :name="iconBeforeModalTitle.name"
                  :width="iconBeforeModalTitle.width"
                  :height="iconBeforeModalTitle.height"
                  :viewBox="iconBeforeModalTitle.viewBox"
                  :maintainColor="iconBeforeModalTitle.maintainColor"
                  class="mr-1"
                />
                <span class="aha-modal__header--text-content">{{ title }}</span>
              </h4>
            </slot>
            <span v-if="closable">
              <button
                type="button"
                @click="onClickCloseButton()"
              >
                <aha-icon
                  name="close"
                  height="20"
                  width="20"
                  viewBox="0 0 24 24"

                  class="cursor-pointer text-base-70 hover:text-primary-90"
                />
              </button>
            </span>
          </div>
          <div v-if="showCustomHeader">
            <component
              :is="componentCustomHeader"
              :onClickCloseButton="onClickCloseButton"
              :title="presentationTitle"
              :headerTitle="headerTitle"
              :headerSubTitle="headerSubTitle"
            />
          </div>
          <div
            class="aha-modal__body"
            id="aha-modal__body"
            :class="[
              {'pt-6': hideHeader},
              `text-${textAlign}`,
              {'aha-modal__body--noMarginPadding': noMarginPadding},
              {'overflow-auto': !visibleOverflow},
              classes
            ]"
          >
            <slot />
          </div>
          <template v-if="$slots.action">
            <div v-if="$slots.additional">
              <slot name="additional" />
            </div>
            <div :class="[actionPosition, actionAlignment]" class="aha-modal__action">
              <slot name="action" />
            </div>
          </template>
        </div>
      </div>
    </transition>
  </portal>
</template>

<script>
import Publish from './custom/header/Publish.vue'
import AhaIcon from './Icon.vue'
import AhaSvg from './Svg.vue'
import Escapable from '../mixins/Escapable.vue'

export default {
  components: {
    AhaIcon,
    AhaSvg,
    Publish,
  },
  mixins: [Escapable],
  props: {
    backable: {
      type: Boolean,
      default: false,
    },
    classes: {
      type: String,
      default: '',
    },
    classesModal: {
      type: [Object, String, Array],
      default: () => ({}),
    },
    closable: {
      type: Boolean,
      default: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    showCustomHeader: {
      type: Boolean,
      default: false,
    },
    componentCustomHeader: {
      type: String,
      default: 'Publish',
    },
    presentationTitle: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'sm',
    },
    title: {
      type: String,
      default: null,
    },
    textAlign: {
      type: String,
      default: 'left',
    },
    actionPosition: {
      type: String,
      default: 'justify-end',
    },
    actionAlignment: {
      type: String,
      default: 'items-center',
    },
    titlePosition: {
      type: String,
      default: '',
    },
    priority: {
      type: String,
      default: 'medium',
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '',
    },
    noScroll: {
      type: Boolean,
      default: false,
    },
    noMarginPadding: {
      type: Boolean,
      default: false,
    },
    blurBackdrop: {
      type: Boolean,
      default: false,
    },
    boxShadow: {
      type: String,
      default: '',
    },
    headerTitle: {
      type: String,
      default: '',
    },
    headerSubTitle: {
      type: String,
      default: '',
    },
    mobileMaxHeight: {
      type: Boolean,
      default: false,
    },
    visibleOverflow: {
      type: Boolean,
      default: false,
    },
    iconBeforeModalTitle: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      modalVisible: false,
    }
  },
  computed: {
    order() {
      if (this.priority === 'medium') return 2

      if (this.priority === 'high') return 3

      return 1
    },
    escapableVisible() {
      return this.modalVisible
    },
  },
  created() {
    this.CLOSE_METHODS = {
      ESC_KEY: 'esc key',
      CLOSE_BUTTON: 'close button',
      BACKDROP_CLICK: 'backdrop click',
    }
  },
  mounted() {
    this.modalVisible = true
  },
  methods: {
    handleBackButtonClick() {
      this.$emit('back')
    },
    onClickBackdrop() {
      if (!this.noCloseOnBackdrop) {
        this.$emit('hide', this.CLOSE_METHODS.BACKDROP_CLICK)
        this.gaTrackEvent('Modal', `Close modal by ${this.CLOSE_METHODS.BACKDROP_CLICK}`, this.title)
      }
    },
    onClickCloseButton() {
      this.$emit('hide', this.CLOSE_METHODS.CLOSE_BUTTON)
      this.gaTrackEvent('Modal', `Close modal by ${this.CLOSE_METHODS.CLOSE_BUTTON}`, this.title)
    },
    onEsc() {
      if (!this.noCloseOnEsc) {
        this.$emit('hide', this.CLOSE_METHODS.ESC_KEY)
        this.gaTrackEvent('Modal', `Close modal by ${this.CLOSE_METHODS.ESC_KEY}`, this.title)
      }
    },
    gaTrackEvent(eventCategory = '', eventAction = '', eventLabel = '') {
      if (window.ga) {
        window.ga('send', 'event', eventCategory, eventAction, eventLabel)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-slide-enter-active,
.modal-slide-leave-active {
  @apply transition-opacity duration-300;

  .aha-modal__content {
    @apply transition-transform duration-300;
  }
}

.modal-slide-enter,
.modal-slide-leave-to {
  @apply opacity-0;

  .aha-modal__content {
    transform: translateY(-200px);
  }
}

.aha-modal {
  @apply fixed inset-0 flex items-center justify-center;
  z-index: 999;
}

.aha-modal__body {
  @apply px-6 mb-5 text-sm break-words;
}

.aha-modal__body--noMarginPadding {
  margin: 0;
  padding: 0;
}

.aha-modal__content {
  @apply relative rounded-lg bg-base-0 max-h-screen overflow-auto;

  &.aha-modal__no-scroll {
    @apply overflow-hidden;
  }

  &.visible-overflow{
    overflow: visible;
  }
}

.aha-modal__content--sm {
  @apply w-108;
}

.aha-modal__content--md {
  @apply w-134;
}

.aha-modal__content--lg {
  @apply w-140;
}

.aha-modal__content--xl {
  width: 40rem;
}

.aha-modal__content--3xl {
  width: 51rem;
}

.aha-modal__content--4xl {
  width: 712px;
}

.aha-modal__content--5xl {
  width: 85%;
  height: 80%;
  @media (max-width: 767px) {
    height: 70%;
  }
}

.aha-modal__content--full-screen {
  @apply w-full h-full rounded-none;

  .aha-modal__body {
    @apply p-0 m-0;
  }
}

.aha-modal__action {
  @apply flex px-6 pb-6;
}

.aha-modal__backdrop {
  @apply absolute inset-0 bg-base-100 opacity-50;
}

.aha-modal__backdrop-blur {
  backdrop-filter: blur(5px);
}

.aha-modal__header {
  @apply flex justify-between py-4 pl-6 pr-3;
}

.aha-modal__header .aha-modal__header--text {
  @apply mb-0 text-base font-bold text-base-100 break-words flex;
  width: calc(100% - 20px);
  .aha-modal__header--text-content {
    @apply inline-block align-middle;
  }
}

.only-mobile-back-button{
  .back-button {
    display: none;
  }
}

@media (max-width: 767.98px) {
  .aha-modal__content {
    &.aha-modal-mobile-max-height {
      height: 100%;

      .aha-modal__body {
        height: 100%;
      }
    }
  }

  .only-mobile-back-button{
    .back-button {
      display: block;
    }
  }
}
</style>
